"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Loader2, Plus, Database, PenLine } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { prechartingService } from "../../apis/prechartingService";
import { isValidIcd10Code, normalizeIcd10Code } from "../../utils/icd10Utils";

function SuggestionRow({ item, onSelect, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(item.code, item.description)}
      className="w-full min-w-0 flex items-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left hover:bg-blue-50 hover:border-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Badge variant="outline" className="font-mono shrink-0 mt-0.5">
        {item.code}
      </Badge>
      <span className="min-w-0 flex-1 text-sm text-gray-700 break-words leading-snug">
        {item.description || "—"}
      </span>
      <Plus className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
    </button>
  );
}

export default function Icd10AddCodeModal({
  open,
  onOpenChange,
  existingCodes = [],
  isReadOnly = false,
  onAdd,
}) {
  const [mode, setMode] = useState("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [manualDescription, setManualDescription] = useState("");
  const searchTimeoutRef = useRef(null);

  const existingSet = useCallback(
    () => new Set(existingCodes.map((c) => normalizeIcd10Code(c))),
    [existingCodes],
  );

  const resetForm = () => {
    setSearchQuery("");
    setSearchResults([]);
    setManualCode("");
    setManualDescription("");
    setMode("search");
  };

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!open || mode !== "search") return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const response = await prechartingService.searchICD10Codes(
          searchQuery.trim(),
          15,
        );
        if (response.success && Array.isArray(response.data)) {
          setSearchResults(
            response.data.filter(
              (item) => !existingSet().has(normalizeIcd10Code(item.code)),
            ),
          );
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error("ICD-10 search error:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, open, mode, existingSet]);

  const handleAdd = async (code, description) => {
    const normalized = normalizeIcd10Code(code);
    if (!isValidIcd10Code(normalized)) {
      toast.error("Enter a valid ICD-10-CM code (e.g. E11.9)");
      return;
    }
    if (existingSet().has(normalized)) {
      toast.error("This code is already in the list");
      return;
    }
    if (!onAdd || isReadOnly) return;

    setIsSaving(true);
    try {
      let desc = (description || "").trim();
      if (!desc) {
        const lookup = await prechartingService.lookupICD10Code(normalized);
        if (lookup.success && lookup.data) {
          desc =
            lookup.data.description ||
            lookup.data.fullDescription ||
            "";
        }
      }

      const ok = await onAdd(normalized, desc);
      if (ok) {
        toast.success(`Added ${normalized}`);
        onOpenChange(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    await handleAdd(manualCode, manualDescription);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-4xl w-[calc(100vw-2rem)] max-h-[92vh] flex-col overflow-x-hidden overflow-y-auto">
        <DialogHeader className="shrink-0 space-y-1 pb-0 text-left">
          <DialogTitle>Add ICD-10 code</DialogTitle>
          <DialogDescription className="text-left">
            Search the ICD-10 database or enter a code manually. The code and
            heading will be added to Assessment & Plan.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex gap-2 border-b border-slate-200 pb-0">
          <button
            type="button"
            onClick={() => setMode("search")}
            className={`flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              mode === "search"
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <Database className="h-4 w-4" />
            Search database
          </button>
          <button
            type="button"
            onClick={() => setMode("manual")}
            className={`flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              mode === "manual"
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <PenLine className="h-4 w-4" />
            Manual entry
          </button>
        </div>

        {mode === "search" ? (
          <div className="mt-4 min-w-0 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="ICD-10 code or condition name…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-11 pl-10 text-base"
                disabled={isReadOnly || isSaving}
                autoFocus
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-xs text-slate-500">
              Type at least 2 characters to search the ICD-10-CM database.
            </p>

            {searchResults.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium text-slate-700">
                  Search results
                </p>
                <div className="min-w-0 space-y-2 rounded-lg border border-slate-200 bg-white p-2">
                  {searchResults.map((item) => (
                    <SuggestionRow
                      key={item.code}
                      item={{
                        code: item.code,
                        description:
                          item.description || item.fullDescription,
                      }}
                      disabled={isReadOnly || isSaving}
                      onSelect={handleAdd}
                    />
                  ))}
                </div>
              </div>
            )}

            {!isSearching &&
              searchQuery.trim().length >= 2 &&
              searchResults.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No codes found. Try another term or use manual entry.
                </p>
              )}
          </div>
        ) : (
          <form
            onSubmit={handleManualSubmit}
            className="mt-4 min-w-0 space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-5"
          >
            <div className="space-y-2">
              <label
                htmlFor="icd-manual-code"
                className="text-xs font-semibold uppercase tracking-wide text-slate-500"
              >
                ICD-10-CM code *
              </label>
              <Input
                id="icd-manual-code"
                type="text"
                placeholder="e.g. E11.9, I10"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                className="font-mono text-base"
                disabled={isReadOnly || isSaving}
                required
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="icd-manual-desc"
                className="text-xs font-semibold uppercase tracking-wide text-slate-500"
              >
                Description (optional)
              </label>
              <Input
                id="icd-manual-desc"
                type="text"
                placeholder="Diagnosis label for Assessment heading"
                value={manualDescription}
                onChange={(e) => setManualDescription(e.target.value)}
                disabled={isReadOnly || isSaving}
              />
              <p className="text-xs text-slate-500">
                If left blank, the description is loaded from the database when
                available.
              </p>
            </div>
            <Button
              type="submit"
              disabled={isReadOnly || isSaving || !manualCode.trim()}
              className="w-full sm:w-auto"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding…
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add code
                </>
              )}
            </Button>
          </form>
        )}

        <div className="mt-4 flex shrink-0 justify-end border-t border-slate-100 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
